import React, { useEffect, useRef, useState } from "react";
import { Sparkles } from "lucide-react";
import { tr } from "../lib/i18n";

export function CategoryNav({ menu, lang, activeId, onSelect, showFeatured, featuredLabel, stickyTop = 0 }) {
  const scrollerRef = useRef(null);
  const itemRefs = useRef({});

  useEffect(() => {
    const el = itemRefs.current[activeId];
    if (!el) return;
    // `inline: "nearest"` scrolls JUST ENOUGH to bring the tab fully into
    // view — no jump if already visible, snaps to the left edge for the
    // first tab and to the right for the last. The `scroll-padding-inline`
    // rule on the scroller keeps a 12px gap so the tab never touches the
    // container edge.
    el.scrollIntoView({ inline: "nearest", block: "nearest", behavior: "smooth" });
  }, [activeId]);

  return (
    <div
      className="sticky z-30 bg-[#f7ebdf]/95 backdrop-blur-md border-b border-[#e8dcc8]"
      data-category-nav
      style={{ top: typeof stickyTop === "number" ? `${stickyTop}px` : stickyTop }}
    >
      <div ref={scrollerRef} className="max-w-3xl mx-auto flex gap-1.5 overflow-x-auto no-scrollbar px-3 py-2.5">
        {showFeatured && (
          <button
            ref={(el) => (itemRefs.current["__featured"] = el)}
            onClick={() => onSelect("__featured")}
            className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full text-[15px] font-semibold whitespace-nowrap transition-colors ${
              activeId === "__featured"
                ? "bg-amber-500 text-white"
                : "bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100"
            }`}
          >
            <Sparkles className="h-4 w-4" /> {featuredLabel}
          </button>
        )}
        {menu.map((cat) => {
          const active = cat.id === activeId;
          return (
            <button
              key={cat.id}
              ref={(el) => (itemRefs.current[cat.id] = el)}
              onClick={() => onSelect(cat.id)}
              className={`shrink-0 px-4 py-2.5 rounded-full text-[15px] font-semibold whitespace-nowrap transition-colors ${
                active ? "bg-[#4a1c17] text-[#f7ebdf]" : "bg-white/60 text-[#2b1410] hover:bg-white border border-transparent hover:border-[#e8dcc8]"
              }`}
            >
              {tr(cat.name, lang)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function useActiveCategory(menu, hasFeatured) {
  const [activeId, setActiveId] = useState(null);
  const lockRef = useRef(0);
  // Pin the highlighted tab while a programmatic scroll is in flight so the
  // scroll-spy doesn't flip through intermediate categories.
  const lockTo = (id, ms = 800) => { lockRef.current = Date.now() + ms; setActiveId(id); };

  useEffect(() => {
    if (!activeId) {
      if (hasFeatured) setActiveId("__featured");
      else if (menu && menu.length > 0) setActiveId(menu[0].id);
    }
  }, [menu, activeId, hasFeatured]);

  useEffect(() => {
    if (!menu || menu.length === 0) return;
    const onScroll = () => {
      if (Date.now() < lockRef.current) return;
      const anchors = hasFeatured ? [{ id: "__featured" }, ...menu] : [...menu];
      const nav = document.querySelector("[data-category-nav]");
      const line = (nav ? nav.getBoundingClientRect().bottom : 140) + 40;
      let current = anchors[0].id;
      for (const cat of anchors) {
        const el = document.getElementById(`cat-${cat.id}`);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top - line <= 0) current = cat.id;
      }
      setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [menu, hasFeatured]);

  return [activeId, setActiveId, lockTo];
}
